import "./shopfrontoffline.scss";
import * as React from "react";
import moment, { type Moment } from "moment";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { getOption } from "../../Utilities/Platform/Options/Options";
import Button from "../Buttons/Button";
import FixedPosition from "../Fixed/FixedPosition";
import ShopfrontLogo from "./ShopfrontLogo";

interface State {
    externalUp: boolean;
    shopfrontUp: boolean;
    externalLastCheckTime: null | Moment;
    shopfrontLastCheckTime: null | Moment;
    lastSyncTime: null | string;
    status: string;
    statusMessage: string;
}

export default class ShopfrontOffline extends React.Component<unknown, State> {
    public state: State = {
        externalUp            : true,
        shopfrontUp           : true,
        externalLastCheckTime : null,
        shopfrontLastCheckTime: null,
        lastSyncTime          : getOption("lastsync"),
        status                : "none",
        statusMessage         : "All Systems Operational",
    };

    protected externalInterval: ReturnType<typeof setInterval> | undefined;
    protected shopfrontInterval: ReturnType<typeof setInterval> | undefined;

    public componentDidMount(): void {
        this.externalInterval = setInterval(this.checkExternal, 1000 * 60);
        this.shopfrontInterval = setInterval(this.checkShopfront, 1000 * 60);

        this.checkExternal();
        this.checkShopfront();
    }

    public componentWillUnmount(): void {
        if(this.externalInterval) {
            clearInterval(this.externalInterval);
        }

        if(this.shopfrontInterval) {
            clearInterval(this.shopfrontInterval);
        }
    }

    protected checkExternal = (): Promise<any> => {
        this.setState({
            externalLastCheckTime: null,
        });

        return fetch("https://xt8pd0mq1tz2.statuspage.io/api/v2/status.json")
            .then(response => {
                if(response.ok) {
                    this.setState({
                        externalUp: true,
                    });
                } else {
                    throw response;
                }

                return response.json();
            })
            .then(body => {
                this.setState({
                    status       : body.status.indicator,
                    statusMessage: body.status.description,
                });
            })
            .catch(() => {
                this.setState({
                    externalUp: false,
                });
            })
            .then(() => {
                this.setState({
                    externalLastCheckTime: moment(),
                });
            });
    };

    protected checkShopfront = (): Promise<void> => {
        this.setState({
            shopfrontLastCheckTime: null,
        });

        return fetch("https://onshopfront.com/areyouonline")
            .then(response => {
                if(response.ok) {
                    this.setState({
                        shopfrontUp: true,
                    });
                } else {
                    throw response;
                }
            })
            .catch(() => {
                this.setState({
                    shopfrontUp: false,
                });
            })
            .then(() => {
                this.setState({
                    shopfrontLastCheckTime: moment(),
                });
            });
    };

    protected viewStatus = (): void => {
        window.location.href = "https://status.onshopfront.com/";
    };

    protected refresh = (): void => {
        window.location.reload();
    };

    public render(): React.ReactNode {
        return (
            <FixedPosition>
                <div className="shopfront-offline">
                    <div className="shopfront-offline-box">
                        <div className="logo">
                            <ShopfrontLogo />
                        </div>
                        <p className="description">
                            Under normal circumstances, you would be able to use Shopfront in offline mode but it
                            appears that your device&apos;s data is out of date and either Shopfront is down
                            or you have no internet.
                        </p>
                        <div className="items">
                            <div className="item">
                                <FontAwesomeLight icon={this.state.externalUp ? "check-circle" : "times-circle"} />
                                You {this.state.externalUp ? "" : "don't"} have internet (last checked
                                on {this.state.externalLastCheckTime === null ? (
                                    <React.Fragment>
                                        Checking Now
                                        <FontAwesomeLight icon="circle-notch" spin />
                                    </React.Fragment>
                                ) : this.state.externalLastCheckTime.format("dddd, MMMM Do YYYY, h:mm:ss a")})
                            </div>
                            <div className="item">
                                <FontAwesomeLight icon={this.state.shopfrontUp ? "check-circle" : "times-circle"} />
                                Shopfront is {this.state.shopfrontUp ? "up" : "down"} (last checked
                                on {this.state.shopfrontLastCheckTime === null ? (
                                    <React.Fragment>
                                        Checking Now
                                        <FontAwesomeLight icon="circle-notch" spin />
                                    </React.Fragment>
                                ) : this.state.shopfrontLastCheckTime.format("dddd, MMMM Do YYYY, h:mm:ss a")})
                            </div>
                            <div className="item">
                                <FontAwesomeLight
                                    icon={this.state.status === "none" ? "check-circle" : "times-circle"}
                                />Shopfront status: {this.state.statusMessage}
                            </div>
                            <div className="item">
                                <FontAwesomeLight
                                    icon={this.state.lastSyncTime !== null ? "check-circle" : "times-circle"}
                                />Your data is {this.state.lastSyncTime === null ? "not" : ""} up to date (last
                                synchronised {
                                    this.state.lastSyncTime === null ?
                                        "never" :
                                        moment(this.state.lastSyncTime).format("dddd, MMMM Do YYYY, h:mm:ss a")
                                })
                            </div>
                        </div>
                        <div className="buttons">
                            <Button onClick={this.viewStatus} classes={[ "button-status" ]}>
                                <FontAwesomeLight icon="traffic-cone" />View Status Page
                            </Button>
                            <Button onClick={this.refresh} classes={[ "button-refresh" ]}>
                                <FontAwesomeLight icon="redo" />Refresh Page
                            </Button>
                        </div>
                    </div>
                </div>
            </FixedPosition>
        );
    }
}
